//src/pages/ForgotPasswordPage.tsx
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  Moon,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";

const NORMEX_LOGO_SRC = "/logo-v3-preto.png";

export default function ForgotPasswordPage() {
  const { forgotPassword, isAuthenticated, isLoading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      toast.error("Informe seu e-mail de acesso.");
      return;
    }

    setIsSubmitting(true);
    try {
      await forgotPassword(normalizedEmail);
      setSubmittedEmail(normalizedEmail);
      toast.success("Se o e-mail existir, enviaremos as instruções.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível enviar o e-mail.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.14),_transparent_34%),linear-gradient(135deg,_hsl(var(--background)),_hsl(var(--muted)/0.35))] p-4 text-foreground">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute right-4 top-4 z-10 rounded-full border border-border/60 bg-background/80 shadow-sm backdrop-blur hover:bg-background"
        aria-label="Alternar tema"
      >
        {theme === "dark" ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </Button>

      <main className="flex min-h-screen items-center justify-center py-8">
        <Card className="w-full max-w-lg overflow-hidden rounded-[2rem] border-border/60 bg-card/95 shadow-2xl shadow-primary/10 backdrop-blur">
          <CardHeader className="space-y-5 px-6 pb-2 pt-8 text-center sm:px-10">
            <div className="flex justify-center">
              <div className="rounded-2xl bg-white px-5 py-3 shadow-sm ring-1 ring-border/50">
                <img
                  src={NORMEX_LOGO_SRC}
                  alt="Normex"
                  className="h-11 w-auto object-contain"
                />
              </div>
            </div>

            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-primary/10 text-primary shadow-lg shadow-primary/10 ring-1 ring-primary/15">
                <ShieldCheck className="h-8 w-8" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-foreground">
                Recuperar senha
              </h1>
              <p className="mx-auto max-w-sm text-base leading-relaxed text-muted-foreground">
                Informe seu e-mail de acesso ao app da <span className="font-semibold text-primary">Normex</span> para receber o link de redefinição.
              </p>
            </div>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-5 px-6 pt-6 sm:px-10">
              {submittedEmail ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-950 dark:text-foreground">Verifique seu e-mail</p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Se o endereço <span className="font-medium text-foreground">{submittedEmail}</span> existir na base,
                        enviaremos instruções para redefinir a senha de acesso ao app da Normex.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="space-y-2 text-left">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-950 dark:text-foreground">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="h-12 rounded-xl text-base"
                  required
                />
              </div>

              <div className="flex items-start gap-4 rounded-2xl border border-primary/15 bg-primary/5 p-4 text-sm text-muted-foreground shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-background text-primary ring-1 ring-primary/15">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="space-y-1.5 text-left">
                  <p className="font-semibold text-slate-950 dark:text-foreground">Link seguro por e-mail</p>
                  <p>O link de redefinição expira em pouco tempo por segurança.</p>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex-col gap-3 px-6 pb-8 pt-2 sm:px-10">
              <Button type="submit" className="h-12 w-full rounded-xl text-base font-semibold" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Enviar link de recuperação
              </Button>

              <Button asChild variant="ghost" className="h-11 w-full rounded-xl font-semibold">
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar para o login
                </Link>
              </Button>
            </CardFooter>
          </form>
        </Card>
      </main>
    </div>
  );
}
